import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { sanitizeEmail, isValidEmail } from '@/lib/security';
import { checkRateLimit, recordAuthAttempt, getRemainingAttempts } from '@/lib/rateLimiting';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { logSecurityEvent } from '@/lib/securityLogging';
import { supabase } from '@/integrations/supabase/client';
import { GoogleSignInButton } from './GoogleSignInButton';
import { AuthDivider } from './AuthDivider';

interface SignInFormProps {
  onSuccess: () => void;
}

export function SignInForm({ onSuccess }: SignInFormProps) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const sanitizedEmail = sanitizeEmail(email);
    if (!isValidEmail(sanitizedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check rate limit before attempting
    const rateLimitCheck = checkRateLimit(sanitizedEmail, 'login');
    if (!rateLimitCheck.allowed) {
      setRateLimitMessage(rateLimitCheck.message || 'Too many attempts. Please try again later.');
      return;
    }

    setRateLimitMessage(null);
    setIsLoading(true);
    
    const { error } = await signIn(sanitizedEmail, password);
    
    if (error) {
      // Record failed attempt
      recordAuthAttempt(sanitizedEmail, 'login', false);
      
      // Log failed login attempt for security monitoring
      // This will be captured by the edge function with IP/UA
      logSecurityEvent({
        eventType: 'admin_login_failed',
        success: false,
        email: sanitizedEmail,
        metadata: { reason: 'invalid_credentials' }
      });
      
      // Check remaining attempts
      const remaining = getRemainingAttempts(sanitizedEmail, 'login');
      
      // Sanitize error message to prevent info leakage
      // Never reveal if email exists or not
      let safeMessage = 'Invalid email or password';
      
      if (error.message.includes('Email not confirmed')) {
        safeMessage = 'Please verify your email address before signing in. Check your inbox for a confirmation link.';
      } else if (remaining <= 2 && remaining > 0) {
        safeMessage = `Invalid email or password. ${remaining} attempt${remaining > 1 ? 's' : ''} remaining before temporary lockout.`;
      }
      
      toast.error(safeMessage);
    } else {
      // Record successful attempt (clears rate limit)
      recordAuthAttempt(sanitizedEmail, 'login', true);
      
      // Check if this is an admin login and log it
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .in('role', ['admin', 'super_admin'])
          .maybeSingle();
        
        if (roleData) {
          // Log admin login success
          logSecurityEvent({
            eventType: 'admin_login',
            success: true,
            email: sanitizedEmail,
            metadata: { role: roleData.role }
          });
        }
      }
      
      toast.success('Signed in successfully!');
      onSuccess();
    }
    
    setIsLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Google Sign In - Primary Option */}
      <GoogleSignInButton onSuccess={onSuccess} disabled={isLoading} />
      
      <AuthDivider />

      {/* Email/Password Form */}
      <form onSubmit={handleSignIn} className="space-y-4">
        {rateLimitMessage && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{rateLimitMessage}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="signin-email">Email</Label>
          <Input
            id="signin-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signin-password">Password</Label>
          <Input
            id="signin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={isLoading}
          />
        </div>
        <Button type="submit" className="w-full h-11" disabled={isLoading || !!rateLimitMessage}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign In with Email
        </Button>
      </form>
    </div>
  );
}
